import { dayjs, type Dayjs } from '@retailer-platform/shared-components/src/utils/datetime/dayjs'
import { type IPP_SUPPORTED_LOCALE } from '@retailer-platform/shared-components/src/utils/intl/intl.types'
import { type QueryParamEncodedType } from './queryParams.types'

const dateFormat = 'YYYY-MM-DD'

/**
 * Checks if the provided string matches the expected dateFormat.
 *
 * @param dateString the string date to test
 * @returns a bool
 */
const isValidDateFormat = (dateString: string) => dayjs(dateString, dateFormat, true).isValid()

/**
 * Safely converts a string into a Day.js instance, if it matches the expected dateFormat, otherwise returns undefined.
 *
 * @param dateString the string date to attempt parsing
 * @returns a Day.js instance or undefined
 */
const stringToDayjs = (dateString: string) =>
  isValidDateFormat(dateString) ? dayjs(dateString, dateFormat) : undefined

export const DayjsDateParam = {
  encode: (dayjsDate: Dayjs) => dayjsDate.format(dateFormat),
  decode: (dateString: QueryParamEncodedType) =>
    stringToDayjs(Array.isArray(dateString) ? dateString[0] : dateString),
}

export const loadDayjsLocale = async (locale: IPP_SUPPORTED_LOCALE) => {
  switch (locale) {
    case 'es-ES':
      try {
        await import('dayjs/locale/es')
        dayjs.locale('es')
      } catch {
        dayjs.locale('en')
      }
      break
    case 'fr-FR':
      try {
        await import('dayjs/locale/fr')
        dayjs.locale('fr')
      } catch {
        dayjs.locale('en')
      }
      break
    case 'en-US':
    default:
      dayjs.locale('en')
      break
  }
}

export const DAYJS_LOCALE_MAPPING: Record<IPP_SUPPORTED_LOCALE, { dayjsLocale: string }> = {
  ['es-ES']: {
    dayjsLocale: 'es',
  },
  ['es-US']: {
    dayjsLocale: 'es',
  },
  ['fr-FR']: {
    dayjsLocale: 'fr',
  },
  ['fr-CA']: {
    dayjsLocale: 'fr',
  },
  ['en-US']: {
    dayjsLocale: 'en',
  },
} as const
